import React, { ReactElement, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import { Telemetry } from '@videoblocks/kafka-rest-client';

import { showPopUp } from '../../../PopUps/actions/PopUpsActions';
import ShareFolderModal from '../../../PopUps/components/ShareFolderModal';
import {
  CONFIRM_ACTION_MODAL,
  SHARE_FOLDER_MODAL,
} from '../../../PopUps/components/types';
import { notify } from '../../../ReactToastify/containers/ReactToastifyContainer';
import SiteConstants from '../../../common/SiteConstants/SiteConstants';
import {
  cloneFolder,
  deleteFolder,
  FOLDER_ACCESS_CONTROL,
  updateFolderAccessControl,
  updateFolderName,
} from '../../MemberBins/actions/FoldersActions';
import FolderCard from '../../MemberBins/components/FolderCard';
import { selectIsInSharedFoldersBeta } from '../../MemberBins/selectors/foldersSelectors';
import { Folder } from '../types';
import NoFoldersHero from './NoFoldersHero';

import './FolderCardsGrid.less';

export interface FolderCardsGridPropTypes {
  dispatch: ThunkDispatch<any, any, any>;
  folders: Folder[];
  isInSharedFoldersBeta?: boolean;
}

function compareFolderDateUpdated(a: Folder, b: Folder): number {
  if (a.dateUpdated < b.dateUpdated) {
    return 1;
  }

  if (a.dateUpdated > b.dateUpdated) {
    return -1;
  }

  return 0;
}

export const FolderCardsGrid = ({
  dispatch,
  folders,
  isInSharedFoldersBeta = false,
}: FolderCardsGridPropTypes): ReactElement => {
  const [shareFolderUniqueId, setShareFolderUniqueId] = useState<string>('');

  const showDeletePopUp = (name, uniqueId) => {
    dispatch(
      showPopUp(CONFIRM_ACTION_MODAL, {
        title: 'Are You Sure?',
        message:
          'You are about to delete ' +
          name +
          '. You will not be able to undo this action.',
        buttonText: 'Delete Folder',
        buttonAction: () => {
          dispatch(deleteFolder(uniqueId));
          notify('Deleted ' + name);
        },
      })
    );
  };

  const showSharePopUp = (uniqueId: string) => {
    Telemetry.increment('folders.sharePopUp');
    const { Intercom } = window as any;

    if (Intercom) {
      Intercom(
        'trackEvent',
        'sharedFolder_'.concat(
          SiteConstants.getInstance().getAbbreviatedSiteName()
        )
      );
    }

    setShareFolderUniqueId(uniqueId);

    dispatch(showPopUp(SHARE_FOLDER_MODAL));
  };

  const showMakeSharedPopUp = (name, uniqueId) => {
    Telemetry.increment('favorite.send-link');
    dispatch(
      showPopUp(CONFIRM_ACTION_MODAL, {
        title: 'Are You Sure?',
        message:
          'You are about to share the folder ' +
          name +
          ' with everyone in your organization. You will not be able to undo this action.',
        buttonText: 'Make Folder Shared',
        buttonAction: () => {
          dispatch(
            updateFolderAccessControl(
              uniqueId,
              name,
              FOLDER_ACCESS_CONTROL.SHARED
            )
          );
        },
      })
    );
  };

  const folderCards = folders
    // Duplicate the array so we do not mutate props. sort() mutates.
    .slice()
    .sort(compareFolderDateUpdated)
    .map((folder) => {
      return (
        <FolderCard
          key={folder.id}
          name={folder.name}
          ownerName={folder.ownerName}
          isOwnedByMe={folder.isOwnedByMe}
          isShared={folder.isShared}
          uniqueId={folder.uniqueId}
          thumbnailUrl={folder.thumbnailUrl}
          itemCount={folder.itemCount}
          isUpdating={folder.isUpdating}
          updateMemberBinName={(uniqueId: string, name: string) =>
            dispatch(updateFolderName(uniqueId, name))
          }
          showDeletePopUp={() => showDeletePopUp(folder.name, folder.uniqueId)}
          showMakeSharedPopUp={() =>
            showMakeSharedPopUp(folder.name, folder.uniqueId)
          }
          showSharePopUp={() => showSharePopUp(folder.uniqueId)}
          cloneMemberBin={(
            uniqueId: string,
            name: string,
            accessControl: string
          ) => {
            dispatch(cloneFolder(uniqueId, name, accessControl));
          }}
          isInSharedFoldersBeta={isInSharedFoldersBeta}
          foldersBasePath="/member/folders"
        />
      );
    });

  return (
    <>
      <ShareFolderModal
        shareUrl={`${
          window.location.origin
        }${SiteConstants.buildInstance().getFoldersFeedbackBasePath()}/${shareFolderUniqueId}`}
        folderUniqueId={shareFolderUniqueId}
      />
      {folderCards.length !== 0 ? (
        <div className="folderCardsGrid">{folderCards}</div>
      ) : (
        <NoFoldersHero isInSharedFoldersBeta={isInSharedFoldersBeta} />
      )}
    </>
  );
};

const FolderCardsGridContainer = ({
  folders,
}: Pick<FolderCardsGridPropTypes, 'folders'>): ReactElement => {
  const isInSharedFoldersBeta = useSelector(selectIsInSharedFoldersBeta);
  return (
    <FolderCardsGrid
      folders={folders}
      isInSharedFoldersBeta={isInSharedFoldersBeta}
      dispatch={useDispatch()}
    />
  );
};

export default FolderCardsGridContainer;
